import React from 'react';
import { Shield, Mail, Phone } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-16 bg-slate-900">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center text-white">
            <Shield className="w-12 h-12 text-yellow-400 mx-auto mb-6" />
            <h1 className="text-4xl font-bold mb-4">Privacy Policy</h1>
            <p className="text-gray-300">Effective Date: March 30, 2025</p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto prose prose-lg">
            <p className="lead">
              Stinger Builds LLC ("we," "our," or "us") respects your privacy and is committed to protecting the personal information you share with us. This Privacy Policy explains how we collect, use, disclose, and safeguard your data when you visit stingerbuilds.com or communicate with us through other means, including SMS/MMS messaging.
            </p>

            <h2>1. Information We Collect</h2>
            
            <h3>a. Personal Information You Provide</h3>
            <ul>
              <li>Full name</li>
              <li>Email address</li>
              <li>Phone number</li>
              <li>Company name</li>
              <li>Project information</li>
              <li>Other messages you submit through forms or communication channels</li>
            </ul>

            <h3>b. Information Collected Automatically</h3>
            <ul>
              <li>IP address</li>
              <li>Browser and device type</li>
              <li>Pages visited and referring URL</li>
              <li>Date/time of visit and interactions</li>
            </ul>

            <h3>c. Communication and Messaging Data</h3>
            <p>
              If you opt in to receive SMS or MMS messages (e.g., through web forms or direct communication), we may collect:
            </p>
            <ul>
              <li>Mobile phone number</li>
              <li>Message content and delivery status</li>
              <li>Timestamps of communication</li>
            </ul>

            <h2>2. How We Use Your Information</h2>
            <p>We use your data to:</p>
            <ul>
              <li>Respond to your inquiries</li>
              <li>Provide our products and services</li>
              <li>Send updates, alerts, or marketing (with consent)</li>
              <li>Improve user experience and website functionality</li>
              <li>Comply with legal requirements and prevent fraud</li>
            </ul>

            <h2>3. Legal Basis for Processing (GDPR)</h2>
            <p>Under the General Data Protection Regulation (GDPR), we process your personal data based on:</p>
            <ul>
              <li>Your consent</li>
              <li>The necessity to fulfill a contract</li>
              <li>Compliance with legal obligations</li>
              <li>Our legitimate interest (e.g., business improvement or security)</li>
            </ul>
            <p>You have the right to withdraw consent at any time.</p>

            <h2>4. Your Rights Under GDPR and CCPA</h2>
            
            <h3>If you are a resident of the European Economic Area (EEA):</h3>
            <ul>
              <li>Right to access, correct, or delete your personal data</li>
              <li>Right to restrict or object to processing</li>
              <li>Right to data portability</li>
              <li>Right to lodge a complaint with your data protection authority</li>
            </ul>

            <h3>If you are a resident of California (CCPA):</h3>
            <ul>
              <li>Right to know what personal data is collected, used, shared, or sold</li>
              <li>Right to request deletion of your personal data</li>
              <li>Right to opt out of the sale of personal data (We do not sell personal data)</li>
              <li>Right to non-discrimination for exercising privacy rights</li>
            </ul>

            <h2>5. SMS/MMS and 10DLC Compliance</h2>
            <p>
              If you provide your mobile number to receive text messages from us, we comply with 10DLC (10-Digit Long Code) rules for A2P (Application-to-Person) messaging, including:
            </p>
            <ul>
              <li>Opt-in Consent: You will only receive text messages if you have given explicit consent.</li>
              <li>Opt-out Mechanism: You can reply STOP at any time to unsubscribe.</li>
              <li>Message Frequency and Content: Message frequency may vary. Message and data rates may apply depending on your carrier.</li>
              <li>Data Handling: We do not share or sell your mobile number or message content to third parties. Messages are stored only as needed for compliance or delivery.</li>
            </ul>

            <h2>6. Data Retention and Security</h2>
            <p>
              We retain your information only as long as necessary for the purposes outlined above, unless a longer retention period is required by law.
            </p>
            <p>
              We use industry-standard measures to protect your data, including encryption, access control, and secure servers.
            </p>

            <div id="cookies" className="scroll-mt-24">
              <h2>7. Cookies and Analytics</h2>
              <p>
                We use cookies and third-party analytics tools (e.g., Google Analytics) to improve your browsing experience.
              </p>
              <p>
                You can manage cookie settings through your browser or use tools like optout.aboutads.info.
              </p>
            </div>

            <h2>8. Third-Party Services</h2>
            <p>
              Our site may contain links to external websites. We are not responsible for their content or privacy practices.
            </p>
            <p>
              We also use third-party service providers to help deliver services. These providers are required to safeguard your data and use it only as directed by us.
            </p>

            <h2>9. Children's Privacy</h2>
            <p>
              We do not knowingly collect personal information from children under the age of 13. If you believe we have, contact us and we will delete it.
            </p>

            <h2>10. Updates to This Privacy Policy</h2>
            <p>
              We may update this policy from time to time. The latest version will always be available on our website with the "Effective Date" noted at the top.
            </p>

            <h2>11. Contact Us</h2>
            <div className="bg-slate-50 p-6 rounded-lg mt-8 mb-8">
              <h3 className="font-semibold mb-4">If you have any questions about this Privacy Policy or wish to exercise your privacy rights, please contact:</h3>
              <div className="space-y-4">
                <p>Stinger Builds LLC<br />2600 W Germann Rd<br />Chandler, AZ 85286</p>
                <div className="flex items-center">
                  <Mail className="w-5 h-5 text-yellow-400 mr-2" />
                  <a href="mailto:contact@stingerbuilds.com" className="text-yellow-600 hover:text-yellow-700">
                    contact@stingerbuilds.com
                  </a>
                </div>
                <div className="flex items-center">
                  <Phone className="w-5 h-5 text-yellow-400 mr-2" />
                  <a href="tel:602-900-1093" className="text-yellow-600 hover:text-yellow-700">
                    602-900-1093
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;